




























import { Component, Prop, Ref, Vue } from 'vue-property-decorator';

@Component
export default class OverflowResizer extends Vue {
  @Prop({ type: String, required: true }) readonly image!: string;
  @Prop({ default: 0 }) readonly top!: number;
  @Prop({ default: 0 }) readonly left!: number;
  @Prop({ type: Number, required: true }) readonly width!: number;
  @Prop({ type: Number, required: true }) readonly height!: number;

  @Ref() readonly host!: HTMLDivElement;

  imageTransform = '';
  contentTransform = '';

  // Resize handler method static reference
  resizeHandler!: () => void;

  mounted() {
    // Setup resize handler
    this.resizeHandler = this.handleResize.bind(this);
    window.addEventListener('resize', this.resizeHandler);

    // Ensure the parent has a set position status
    if (this.parent && !this.parent.style.position) {
      this.parent.style.position = 'relative';
    }

    // Initial sizing calculations
    this.handleResize();
    this.ensureCalculation(); 
  }

  destroyed() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  /**
   * This method should account for slow internet connections or weaker computers,
   * it's only purpose is to re-run until the browser is ready to handle the
   * resize calculations. Just putting this here as a "just in case".
   */
  ensureCalculation() {
    if (!this.imageTransform || !this.contentTransform) {
      this.handleResize();
      setTimeout(this.ensureCalculation.bind(this), 100);
    }
  }

  /**
   * Bound to window resize. Automatically calculates transform string to keep
   * image content area centered and visible. Also keeps component slotted content
   * perfectly scaled and centered over image content area.
   */
  handleResize() {
    if (!this.parent) return;

    // Get parent dimensions
    const { width, height } = this.parent.getBoundingClientRect();
    
    // Determine transform scale
    const scale = Math.min(width / this.width, height / this.height);

    // Determine image position offsets based on new scale
    let imageLeft = this.left * scale;
    let imageTop = this.top * scale;

    // Calculate rendered size
    const outputWidth = this.width * scale;
    const outputHeight = this.height * scale;

    // Determine padding sizes to center content
    const padding = this.calculateRemainingSpace(width, height, outputWidth, outputHeight);

    // Subtract from existing values for image positions in order to account for existing padding
    imageLeft = (imageLeft - padding.horizontal) * -1;
    imageTop = (imageTop - padding.vertical) * -1;

    // Apply transformations
    this.imageTransform = `translate(${imageLeft}px, ${imageTop}px) scale(${scale})`;
    this.contentTransform = `translate(${padding.horizontal}px, ${padding.vertical}px) scale(${scale})`;
  }

  /**
   * Returns the amount of extra vertical and horizontal space there is
   * given the size of rectangular container (width, height) and a rectangular
   * child (contentWidth, contentHeight)
   */
  calculateRemainingSpace(width: number, height: number, contentWidth: number, contentHeight: number) {
    return {
      horizontal: width > contentWidth ? (width - contentWidth) / 2 : 0,
      vertical: height > contentHeight ? (height - contentHeight) / 2 : 0
    };
  }

  get parent() {
    return this.host?.parentElement;
  }
}
